import { useCallback, useEffect, useRef, useState } from "react";
import uuid4 from "uuid4/browser";

import { useUser } from "@js/apps/common/hooks";
import { WebSocketManagerContainer } from "@js/components/websocket";
import { useWebsocket } from "@js/hooks";
import type { BaseMessage } from "@js/types/messenger";
import { formatDate, getToday } from "@js/utils";

type sendMessageFunCallbacks = {
  onSent?: () => void;
  onDelivered?: () => void;
  onCannotSend?: () => void;
};

type UseSendMessageType = {
  room?: number;
  canSendMessage?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ListenerType = Record<string, (data: any) => void>;

type ContentType = {
  message?: string;
  budget?: string;
  calendar_link?: string;
  attachment_ids?: number[];
};

const USE_SEND_MESSAGE_LISTENER_ID = "use-send-message-listener";

export const useSendMessage = ({
  room,
  canSendMessage,
}: UseSendMessageType) => {
  const user = useUser();
  const { send, isRunning } = useWebsocket();
  const [messageError, setMessageError] = useState<string | null>(null);

  const subscribers = useRef<ListenerType>({});

  const notify = useCallback(
    ({
      data,
    }: {
      data: {
        broadcast_type: keyof typeof ENUMS.BroadcastType;
        error_message?: string;
        data: unknown;
      };
    }) => {
      if (data.broadcast_type !== ENUMS.BroadcastType.MESSAGE_STATUS) {
        return;
      }

      if (data.error_message) {
        setMessageError(data.error_message);
      } else if (messageError) {
        setMessageError(null);
      }
      Object.keys(subscribers.current).forEach((key) => {
        subscribers.current[key](data);
      });
    },
    [messageError, subscribers],
  );

  useEffect(() => {
    WebSocketManagerContainer.attachListener(
      notify,
      USE_SEND_MESSAGE_LISTENER_ID,
    );

    return () => {
      WebSocketManagerContainer.removeListener(USE_SEND_MESSAGE_LISTENER_ID);
    };
  }, [notify]);

  useEffect(() => {
    setMessageError(null);
  }, [room]);

  const canSend =
    typeof canSendMessage === "boolean"
      ? canSendMessage
      : !!user && !!isRunning && !!room;

  const sendMessage = (
    content: ContentType,
    callbacks?: sendMessageFunCallbacks,
    authorId?: number,
  ) => {
    const {
      onSent = () => {
        /* empty  */
      },
      onDelivered = () => {
        /* empty  */
      },
      onCannotSend = () => {
        /* empty  */
      },
    } = callbacks || {};

    if (
      (canSend && content.message && content.message.trim().length > 0) ||
      content.calendar_link?.trim() ||
      (content.attachment_ids && content.attachment_ids?.length > 0)
    ) {
      const messageMock = createMessageMock(content);
      const id = messageMock.optimistic_id;

      const listener = <T extends BaseMessage>(payload: T) => {
        if (payload.optimistic_id === id) {
          onDelivered();
          delete subscribers.current[id];
        }
      };

      subscribers.current[id] = listener;

      send(ENUMS.StreamType.MESSAGE, {
        room: messageMock.room,
        message_content: messageMock.content.message || null,
        budget: messageMock.content.budget,
        calendar_link: messageMock.content.calendar_link,
        attachments_ids: messageMock.content.attachment_ids,
        optimistic_id: id,
        author: authorId,
      });

      onSent();
    }

    if (!canSend) {
      onCannotSend();
    }
  };

  const createMessageMock = useCallback(
    (content: ContentType) => {
      const uuid = uuid4();

      return {
        id: uuid,
        room,
        author: user,
        content,
        sent: formatDate(getToday(), undefined),
        created: null,
        optimistic_id: uuid,
      };
    },
    [user, room],
  );

  return {
    sendMessage,
    canSendMessage: canSend,
    messageError,
  };
};
